
import React, { useEffect, useRef } from 'react';
import CTASection from '@/components/CTASection';
import PersonalPricingSection from "@/components/pricing/PersonalPricingSection";
import OutdoorCoachingSection from "@/components/pricing/OutdoorCoachingSection";
import BusinessPricingSection from "@/components/pricing/BusinessPricingSection";
import { ArrowRight, TrendingUp, Users, Briefcase } from 'lucide-react';
import TestimonialsSection from '@/components/coaching/TestimonialsSection';
import PreiseFAQ from '@/components/preise/PreiseFAQ';
import SEOMetadata from '@/components/SEOMetadata';
import { keywordClusters } from '@/data/seo/keywords';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { webPageStructuredData } from '@/data/structuredData';

const Preise = () => {
  // Add refs for the sections
  const privatpersonenRef = useRef<HTMLDivElement>(null);
  const outdoorRef = useRef<HTMLDivElement>(null);
  const unternehmenRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  
  // Robust hash-scrolling for /preise#faq with retries to handle late layout shifts
  useEffect(() => {
    if (location.hash === '#faq') {
      const scrollToFAQ = () => {
        const el = document.getElementById('faq');
        if (!el) return false;
        const headerOffset = 120;
        const y = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
        return true;
      };

      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (scrollToFAQ() || attempts >= 10) {
          clearInterval(interval);
        }
      }, 200);

      return () => clearInterval(interval);
    } else {
      // Bei normalen Aufrufen (ohne Hash) zum Anfang scrollen
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetRef: React.RefObject<HTMLDivElement>) => {
    e.preventDefault();
    
    if (targetRef.current) {
      const offsetTop = targetRef.current.offsetTop;
      const headerOffset = 100; // Adjust header offset as needed
      
      window.scrollTo({
        top: offsetTop - headerOffset,
        behavior: 'smooth'
      });
    }
  };
  
  return (
    <>
      <SEOMetadata
        metaTitle="Coaching-Preise: Finanz- & Executive Coaching"
        metaDescription="Transparente Preise: Finanzcoaching ab 150€/h, Executive Coaching ab 200€/h. Paketpreise verfügbar. Kostenloses Erstgespräch."
        canonicalUrl="https://joerg-hoermann.de/preise"
        title="Coaching-Preise: Transparent und Fair"
        description="Transparente Preisgestaltung für alle Coaching-Angebote. Honorarbasierte Abrechnung ohne versteckte Kosten oder Produktverkauf."
        pageName="Preise"
        primaryLink="/preise"
        keywords={keywordClusters.homepage.join(', ')}
        imageUrl="/images/og-portrait.jpg"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(webPageStructuredData({
            name: "Coaching-Preise: Transparent und Fair",
            description: "Transparente Preise für Finanzcoaching, Executive Coaching und Outdoor-Coaching. Paketpreise und Einzelsitzungen.",
            url: "https://joerg-hoermann.de/preise"
          }))}
        </script>
      </Helmet>
      <main>
      {/* AI-Summary / AEO Abstract */}
      <section className="sr-only" aria-label="Zusammenfassung" data-speakable="summary">
        <p>Transparente Preisgestaltung für alle Coaching-Angebote von Jörg Hörmann: Finanzcoaching ab 150€/h, Executive Coaching ab 200€/h. Honorarbasierte Abrechnung ohne versteckte Kosten oder Produktverkauf. Paketpreise und kostenloses Erstgespräch verfügbar.</p>
      </section>
      {/* Section navigation at the top */}
      <section id="pricing-navigation" className="section-spacing bg-white border-b border-joerg-100/50">
        <div className="container max-w-5xl">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <h1 className="text-left mb-4" id="angebotsvergleich">
              Meine Coaching-Angebote
            </h1>
            <p className="text-joerg-600 text-left mb-6">
              Wählen Sie das passende Angebot für Ihre individuellen Bedürfnisse – ob als Privatperson, im Outdoor-Coaching oder für Unternehmen.
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row justify-center gap-4 md:gap-8">
            <a 
              href="#privatpersonen" 
              onClick={(e) => handleNavClick(e, privatpersonenRef)}
              className="group flex items-center gap-3 p-4 rounded-lg border border-joerg-100 hover:border-joerg-300 hover:bg-joerg-50/50 transition-all"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#E8F1FB] text-joerg-700 group-hover:scale-110 transition-transform">
                <Users className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-joerg-800">Privatpersonen</h3>
                <p className="text-sm text-joerg-600">Individuelle Coaching-Angebote</p>
              </div>
              <ArrowRight className="h-4 w-4 text-joerg-400 group-hover:text-joerg-700 group-hover:translate-x-1 transition-all" />
            </a>
            
            <a 
              href="#outdoor" 
              onClick={(e) => handleNavClick(e, outdoorRef)}
              className="group flex items-center gap-3 p-4 rounded-lg border border-joerg-100 hover:border-joerg-300 hover:bg-joerg-50/50 transition-all"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EDF5E9] text-[#2F855A] group-hover:scale-110 transition-transform">
                <TrendingUp className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-joerg-800">Outdoor-Coaching</h3>
                <p className="text-sm text-joerg-600">Coaching in der Natur</p>
              </div>
              <ArrowRight className="h-4 w-4 text-joerg-400 group-hover:text-joerg-700 group-hover:translate-x-1 transition-all" />
            </a>
            
            <a 
              href="#unternehmen" 
              onClick={(e) => handleNavClick(e, unternehmenRef)}
              className="group flex items-center gap-3 p-4 rounded-lg border border-joerg-100 hover:border-joerg-300 hover:bg-joerg-50/50 transition-all"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#E9E6F4] text-primary group-hover:scale-110 transition-transform">
                <Briefcase className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-joerg-800">Unternehmen</h3>
                <p className="text-sm text-joerg-600">Führungskräfte & Teams</p>
              </div>
              <ArrowRight className="h-4 w-4 text-joerg-400 group-hover:text-joerg-700 group-hover:translate-x-1 transition-all" />
            </a>
          </div>
        </div>
      </section>

      {/* Section for Privatpersonen */}
      <div ref={privatpersonenRef} id="privatpersonen">
        <PersonalPricingSection />
        
        {/* Testimonials Section moved here to be associated with private clients */}
        <TestimonialsSection />
      </div>

      {/* Section for Outdoor-Coaching */}
      <div ref={outdoorRef} id="outdoor">
        <OutdoorCoachingSection />
      </div>

      {/* Section for Unternehmen */}
      <div ref={unternehmenRef} id="unternehmen">
        <BusinessPricingSection />
      </div>
      
      <PreiseFAQ />
      
      <CTASection
        title="Bereit für den nächsten Schritt?"
        description="Vereinbaren Sie ein kostenfreies Erstgespräch, um herauszufinden, wie ich Sie unterstützen kann."
        primaryCTA={{ 
          text: "Gespräch vereinbaren", 
          link: "https://zeeg.me/joerghoermann",
          isExternal: true 
        }}
        backgroundClass="bg-gradient-to-br from-[#EDF5E9] to-[#E8F1FB] text-joerg-800"
      />
    </main>
    </>
  );
};

export default Preise;
